import { createFileRoute, Link, useRouter } from '@tanstack/react-router';
import { format, formatDistanceToNowStrict } from 'date-fns';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import {
    CartesianGrid,
    Legend,
    Line,
    LineChart,
    ResponsiveContainer,
    Tooltip as RechartsTooltip,
    XAxis,
    YAxis,
} from 'recharts';

import { Button } from '@apps/web/components/ui/button';
import {
    Card,
    CardContent,
    CardHeader,
    CardTitle,
} from '@apps/web/components/ui/card';
import { DataTable } from '@apps/web/components/ui/data_table';
import { Input } from '@apps/web/components/ui/input';
import { Label } from '@apps/web/components/ui/label';
import { MultiSelect } from '@apps/web/components/ui/multi_select';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@apps/web/components/ui/select';
import { Switch } from '@apps/web/components/ui/switch';
import {
    Tooltip,
    TooltipContent,
    TooltipTrigger,
} from '@apps/web/components/ui/tooltip';
import { type ColumnDef } from '@apps/web/lib/table';
import { useDebouncedValue } from '@apps/web/lib/use_debounced_value';
import {
    EXECUTION_BACKEND_OPTIONS,
    EXECUTION_STATUS_OPTIONS,
    FAMILY_OPTIONS,
    OUTCOME_OPTIONS,
    PER_PAGE,
    SOURCE_OPTIONS,
    voiceEvalRunsSearchSchema,
} from '@apps/web/routes/_app/admin/voice-evals/search_params';
import {
    EvidenceFamilyBadge,
    EXECUTION_BACKEND_LABELS,
    EXECUTION_STATUS_LABELS,
    FAMILY_LABELS,
    FlakyChip,
    formatDateTime,
    formatLatencyMs,
    formatPercent,
    formatUsd,
    GitProvenanceCell,
    RunOutcomeBadge,
    SOURCE_LABELS,
    SourceBadge,
} from '@apps/web/routes/_app/admin/voice-evals/shared';
import {
    useVoiceEvalRuns,
    useVoiceEvalRunStats,
    useVoiceEvalTrends,
} from '@apps/web/swr_data_fetching/data_fetching_hooks';
import {
    type EvalRunListItem,
    type EvalRunSource,
    type EvalRunTrendDay,
} from '@libraries/typescript/api_client';

const SEARCH_DEBOUNCE_MS = 300;
const CLEAN_CI_DAILY_SOURCE: Array<EvalRunSource> = ['ci_daily'];
const TREND_WINDOW_OPTIONS = [30, 60, 90] as const;

export const Route = createFileRoute('/_app/admin/voice-evals/')({
    head: () => ({
        meta: [{ title: 'Voice Evals | Phoebe Admin' }],
    }),
    component: VoiceEvalRunsPage,
    validateSearch: voiceEvalRunsSearchSchema,
});

const OUTCOME_LABELS: Record<(typeof OUTCOME_OPTIONS)[number], string> = {
    all_passed: 'Passing',
    has_failures: 'Failing',
    flaky: 'Flaky',
};

function StatCard({
    label,
    value,
}: {
    readonly label: string;
    readonly value: string;
}) {
    return (
        <Card>
            <CardHeader className="pb-2">
                <CardTitle className="text-muted-foreground text-sm font-medium">
                    {label}
                </CardTitle>
            </CardHeader>
            <CardContent>
                <div className="text-2xl font-semibold">{value}</div>
            </CardContent>
        </Card>
    );
}

function RunTimeCell({ run }: { readonly run: EvalRunListItem }) {
    const timestamp = run.finished_at ?? run.updated_at ?? run.created_at;
    return (
        <Tooltip>
            <TooltipTrigger asChild={true}>
                <span className="text-sm whitespace-nowrap">
                    {formatDistanceToNowStrict(new Date(timestamp), {
                        addSuffix: true,
                    })}
                </span>
            </TooltipTrigger>
            <TooltipContent>{formatDateTime(timestamp)}</TooltipContent>
        </Tooltip>
    );
}

type DailyPassRatePoint = {
    readonly timestamp: number;
    readonly passRate: number | null;
    readonly rollingPassRate: number | null;
    readonly day: EvalRunTrendDay;
};

function toPercentValue(value: number | null): number | null {
    return value === null ? null : Math.round(value * 1000) / 10;
}

function toDailyPassRatePoints(
    days: ReadonlyArray<EvalRunTrendDay>,
): Array<DailyPassRatePoint> {
    return days.map((day) => ({
        timestamp: new Date(day.day).getTime(),
        passRate: toPercentValue(day.pass_rate),
        rollingPassRate: toPercentValue(day.rolling_pass_rate),
        day,
    }));
}

type DailyPassRateTooltipProps = {
    readonly active?: boolean;
    readonly payload?: ReadonlyArray<{
        readonly payload: DailyPassRatePoint;
    }>;
};

function DailyPassRateTooltip({ active, payload }: DailyPassRateTooltipProps) {
    const point = payload?.[0]?.payload;
    if (!active || !point) {
        return null;
    }
    const { day } = point;
    return (
        <div className="bg-popover text-popover-foreground max-w-xs rounded-md border p-2 text-xs shadow-md">
            <div className="font-medium">
                {format(new Date(point.timestamp), 'MMM d, yyyy')} ·{' '}
                {formatPercent(day.pass_rate)} pass
            </div>
            <div className="text-muted-foreground mt-1">
                {day.runs.toLocaleString()} runs ·{' '}
                {day.total_passed.toLocaleString()} of{' '}
                {day.total_attempted.toLocaleString()} passed
            </div>
            <div className="text-muted-foreground">
                {day.flaky_cases.toLocaleString()} flaky ·{' '}
                {day.failed_cases.toLocaleString()} failed
            </div>
            <div className="text-muted-foreground">
                {formatUsd(day.total_cost_usd)} · avg latency{' '}
                {formatLatencyMs(day.avg_latency_ms)}
            </div>
        </div>
    );
}

function DailyPassRateChart({
    days,
    rollingWindowDays,
    isLoading,
}: {
    readonly days: ReadonlyArray<EvalRunTrendDay>;
    readonly rollingWindowDays: number;
    readonly isLoading: boolean;
}) {
    const points = toDailyPassRatePoints(days);
    if (isLoading) {
        return (
            <div className="text-muted-foreground flex h-64 items-center justify-center text-sm">
                Loading daily pass rate…
            </div>
        );
    }
    if (points.length === 0) {
        return (
            <div className="text-muted-foreground flex h-64 items-center justify-center text-sm">
                No trend data for this window.
            </div>
        );
    }
    return (
        <ResponsiveContainer width="100%" height={256}>
            <LineChart
                data={points}
                margin={{ top: 8, right: 16, bottom: 0, left: 0 }}
            >
                <CartesianGrid stroke="var(--border)" vertical={false} />
                <XAxis
                    dataKey="timestamp"
                    type="number"
                    scale="time"
                    domain={['dataMin', 'dataMax']}
                    tickFormatter={(value: number) =>
                        format(new Date(value), 'M/d')
                    }
                    stroke="var(--muted-foreground)"
                    fontSize={11}
                    tickLine={false}
                />
                <YAxis
                    yAxisId="left"
                    domain={[0, 100]}
                    tickFormatter={(value: number) => `${value}%`}
                    stroke="var(--muted-foreground)"
                    fontSize={11}
                    tickLine={false}
                    axisLine={false}
                    width={40}
                />
                <RechartsTooltip content={<DailyPassRateTooltip />} />
                <Legend wrapperStyle={{ fontSize: 12 }} iconType="circle" />
                <Line
                    yAxisId="left"
                    name="Pass rate"
                    dataKey="passRate"
                    stroke="var(--chart-1)"
                    strokeWidth={2}
                    dot={{ r: 3, fill: 'var(--chart-1)' }}
                    connectNulls={true}
                    isAnimationActive={false}
                />
                <Line
                    yAxisId="left"
                    name={`${rollingWindowDays}-day average`}
                    dataKey="rollingPassRate"
                    stroke="var(--chart-2)"
                    strokeWidth={2}
                    strokeDasharray="4 4"
                    dot={false}
                    connectNulls={true}
                    isAnimationActive={false}
                />
            </LineChart>
        </ResponsiveContainer>
    );
}

const columns: Array<ColumnDef<EvalRunListItem>> = [
    {
        id: 'finished_at',
        header: 'Updated',
        cell: ({ row }) => <RunTimeCell run={row.original} />,
    },
    {
        id: 'outcome',
        header: 'Outcome',
        cell: ({ row }) => (
            <div className="flex items-center gap-1.5">
                <RunOutcomeBadge run={row.original} />
                <FlakyChip count={row.original.summary.flaky_cases} />
            </div>
        ),
    },
    {
        id: 'cases',
        header: 'Cases',
        cell: ({ row }) => {
            const { summary } = row.original;
            return (
                <span className="text-sm whitespace-nowrap">
                    {summary.total_passed}/{summary.total_attempted} passed
                    {summary.total_skipped > 0 &&
                        ` · ${summary.total_skipped} skipped`}
                </span>
            );
        },
    },
    {
        id: 'source',
        header: 'Source',
        cell: ({ row }) => <SourceBadge source={row.original.source} />,
    },
    {
        id: 'family',
        header: 'Evidence',
        cell: ({ row }) => <EvidenceFamilyBadge family={row.original.family} />,
    },
    {
        id: 'selector',
        header: 'Selector',
        cell: ({ row }) => (
            <span className="block max-w-56 truncate font-mono text-xs">
                {row.original.suite_selector ?? '—'}
            </span>
        ),
    },
    {
        id: 'git',
        header: 'Git',
        cell: ({ row }) => <GitProvenanceCell run={row.original} />,
    },
    {
        id: 'model',
        header: 'Model',
        cell: ({ row }) => (
            <span className="text-muted-foreground text-xs whitespace-nowrap">
                {row.original.provider ?? '—'}
                {row.original.model && ` / ${row.original.model}`}
            </span>
        ),
    },
    {
        id: 'triggered_by',
        header: 'Triggered by',
        cell: ({ row }) => (
            <span className="text-muted-foreground text-xs">
                {row.original.triggered_by ?? '—'}
            </span>
        ),
    },
];

function VoiceEvalRunsPage() {
    const router = useRouter();
    const search = Route.useSearch();
    const { page } = search;

    const routeSearchValue = search.search ?? '';
    const [searchDraft, setSearchDraft] = useState(routeSearchValue);
    const debouncedSearch = useDebouncedValue(searchDraft, SEARCH_DEBOUNCE_MS);
    const [trendWindowDays, setTrendWindowDays] = useState(30);
    const [followPageFilters, setFollowPageFilters] = useState(false);
    // Only user edits may navigate; URL changes (back/forward, shared
    // links) resync the draft instead of being overwritten by it.
    const searchDraftEditedRef = useRef(false);

    useEffect(() => {
        searchDraftEditedRef.current = false;
        setSearchDraft(routeSearchValue);
    }, [routeSearchValue]);

    useEffect(() => {
        if (!searchDraftEditedRef.current) {
            return;
        }
        const trimmed = debouncedSearch.trim() || undefined;
        if ((routeSearchValue.trim() || undefined) === trimmed) {
            return;
        }
        void router.navigate({
            to: '/admin/voice-evals',
            search: { ...search, search: trimmed, page: 1 },
        });
    }, [debouncedSearch, routeSearchValue, router, search]);

    const queryParams = useMemo(
        () => ({
            page,
            per_page: PER_PAGE,
            source: search.source,
            family: search.family,
            execution_status: search.execution_status,
            execution_backend: search.execution_backend,
            outcome: search.outcome,
            clean_only: search.clean_only,
            git_branch: search.git_branch,
            git_commit: search.git_commit,
            model: search.model,
            created_by_user_id: search.created_by_user_id,
            search: search.search,
            sort_by: search.sort_by,
            sort_order: search.sort_order,
        }),
        [page, search],
    );
    const statsParams = useMemo(() => {
        const {
            page: _page,
            per_page: _perPage,
            sort_by: _sortBy,
            sort_order: _sortOrder,
            ...filterParams
        } = queryParams;
        return filterParams;
    }, [queryParams]);
    const trendParams = useMemo(() => {
        if (followPageFilters) {
            return {
                ...statsParams,
                window_days: trendWindowDays,
            };
        }
        return {
            window_days: trendWindowDays,
            source: CLEAN_CI_DAILY_SOURCE,
            clean_only: true,
        };
    }, [followPageFilters, statsParams, trendWindowDays]);

    const { data, isLoading } = useVoiceEvalRuns(queryParams);
    const { data: stats } = useVoiceEvalRunStats(statsParams);
    const { data: trends, isLoading: trendsIsLoading } =
        useVoiceEvalTrends(trendParams);

    const items = data?.items ?? [];
    const total = data?.total ?? 0;
    const totalPages = Math.max(1, Math.ceil(total / PER_PAGE));

    return (
        <div className="flex h-full flex-col">
            <div className="border-border flex items-start justify-between border-b px-4 py-3">
                <div>
                    <h1 className="text-lg font-semibold">Voice Evals</h1>
                    <p className="text-muted-foreground text-sm">
                        Shared, cloud-first eval history across every voice
                        evidence family.
                    </p>
                </div>
                <div className="flex items-center gap-2">
                    <Button variant="outline" size="sm" asChild={true}>
                        <Link to="/admin/voice-evals/compare">
                            Compare runs
                        </Link>
                    </Button>
                    <Button variant="outline" size="sm" asChild={true}>
                        <Link to="/admin/voice-evals/catalog">
                            Catalog &amp; coverage
                        </Link>
                    </Button>
                </div>
            </div>
            <div className="grid grid-cols-2 gap-3 p-4 md:grid-cols-4 xl:grid-cols-7">
                <StatCard
                    label="Runs"
                    value={stats ? String(stats.total_runs) : '—'}
                />
                <StatCard
                    label="Passing"
                    value={stats ? String(stats.passing_runs) : '—'}
                />
                <StatCard
                    label="Failing"
                    value={stats ? String(stats.failing_runs) : '—'}
                />
                <StatCard
                    label="With flaky cases"
                    value={stats ? String(stats.flaky_runs) : '—'}
                />
                <StatCard
                    label="In progress"
                    value={stats ? String(stats.in_progress_runs) : '—'}
                />
                <StatCard
                    label="Execution failed"
                    value={stats ? String(stats.execution_failed_runs) : '—'}
                />
                <StatCard
                    label="Avg pass rate"
                    value={stats ? formatPercent(stats.avg_pass_rate) : '—'}
                />
            </div>
            <div className="px-4 pb-4">
                <Card>
                    <CardHeader className="flex-row items-center justify-between space-y-0 pb-2">
                        <CardTitle className="text-sm font-medium">
                            Daily pass rate
                        </CardTitle>
                        <div className="flex flex-wrap items-center gap-3">
                            <Select
                                value={String(trendWindowDays)}
                                onValueChange={(value) =>
                                    setTrendWindowDays(Number(value))
                                }
                            >
                                <SelectTrigger className="h-8 w-28">
                                    <SelectValue placeholder="Window" />
                                </SelectTrigger>
                                <SelectContent>
                                    {TREND_WINDOW_OPTIONS.map((windowDays) => (
                                        <SelectItem
                                            key={windowDays}
                                            value={String(windowDays)}
                                        >
                                            {windowDays} days
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                            <div className="flex items-center gap-2">
                                <Switch
                                    id="trend-follow-filters"
                                    checked={followPageFilters}
                                    onCheckedChange={setFollowPageFilters}
                                />
                                <Label
                                    htmlFor="trend-follow-filters"
                                    className="text-sm font-normal"
                                >
                                    {followPageFilters
                                        ? 'Follow filters'
                                        : 'CI daily (clean)'}
                                </Label>
                            </div>
                        </div>
                    </CardHeader>
                    <CardContent>
                        <DailyPassRateChart
                            days={trends?.days ?? []}
                            rollingWindowDays={trends?.rolling_window_days ?? 7}
                            isLoading={trendsIsLoading}
                        />
                    </CardContent>
                </Card>
            </div>
            <div className="flex flex-wrap items-center gap-3 px-4 pb-3">
                <Input
                    value={searchDraft}
                    onChange={(event) => {
                        searchDraftEditedRef.current = true;
                        setSearchDraft(event.target.value);
                    }}
                    placeholder="Search branch, commit, selector, worktree…"
                    className="h-8 w-72"
                />
                <MultiSelect
                    className="w-44"
                    options={SOURCE_OPTIONS.map((source) => ({
                        value: source,
                        label: SOURCE_LABELS[source],
                    }))}
                    selected={[...(search.source ?? [])]}
                    onChange={(selected) => {
                        const sources = SOURCE_OPTIONS.filter((source) =>
                            selected.includes(source),
                        );
                        void router.navigate({
                            to: '/admin/voice-evals',
                            search: {
                                ...search,
                                source: sources.length ? sources : undefined,
                                page: 1,
                            },
                        });
                    }}
                    compactLabel="Source"
                />
                <MultiSelect
                    className="w-48"
                    options={FAMILY_OPTIONS.map((family) => ({
                        value: family,
                        label: FAMILY_LABELS[family],
                    }))}
                    selected={[...(search.family ?? [])]}
                    onChange={(selected) => {
                        const families = FAMILY_OPTIONS.filter((family) =>
                            selected.includes(family),
                        );
                        void router.navigate({
                            to: '/admin/voice-evals',
                            search: {
                                ...search,
                                family: families.length ? families : undefined,
                                page: 1,
                            },
                        });
                    }}
                    compactLabel="Evidence family"
                />
                <MultiSelect
                    className="w-48"
                    options={EXECUTION_STATUS_OPTIONS.map(
                        (executionStatus) => ({
                            value: executionStatus,
                            label: EXECUTION_STATUS_LABELS[executionStatus],
                        }),
                    )}
                    selected={[...(search.execution_status ?? [])]}
                    onChange={(selected) => {
                        const statuses = EXECUTION_STATUS_OPTIONS.filter(
                            (executionStatus) =>
                                selected.includes(executionStatus),
                        );
                        void router.navigate({
                            to: '/admin/voice-evals',
                            search: {
                                ...search,
                                execution_status: statuses.length
                                    ? statuses
                                    : undefined,
                                page: 1,
                            },
                        });
                    }}
                    compactLabel="Execution status"
                />
                <MultiSelect
                    className="w-40"
                    options={EXECUTION_BACKEND_OPTIONS.map((backend) => ({
                        value: backend,
                        label: EXECUTION_BACKEND_LABELS[backend],
                    }))}
                    selected={[...(search.execution_backend ?? [])]}
                    onChange={(selected) => {
                        const backends = EXECUTION_BACKEND_OPTIONS.filter(
                            (backend) => selected.includes(backend),
                        );
                        void router.navigate({
                            to: '/admin/voice-evals',
                            search: {
                                ...search,
                                execution_backend: backends.length
                                    ? backends
                                    : undefined,
                                page: 1,
                            },
                        });
                    }}
                    compactLabel="Executor"
                />
                <Select
                    value={search.outcome ?? 'all'}
                    onValueChange={(value) =>
                        void router.navigate({
                            to: '/admin/voice-evals',
                            search: {
                                ...search,
                                outcome: OUTCOME_OPTIONS.find(
                                    (option) => option === value,
                                ),
                                page: 1,
                            },
                        })
                    }
                >
                    <SelectTrigger className="h-8 w-36">
                        <SelectValue placeholder="Outcome" />
                    </SelectTrigger>
                    <SelectContent>
                        <SelectItem value="all">All outcomes</SelectItem>
                        {OUTCOME_OPTIONS.map((outcome) => (
                            <SelectItem key={outcome} value={outcome}>
                                {OUTCOME_LABELS[outcome]}
                            </SelectItem>
                        ))}
                    </SelectContent>
                </Select>
                <div className="flex items-center gap-2">
                    <Switch
                        id="clean-only"
                        checked={search.clean_only ?? false}
                        onCheckedChange={(checked) =>
                            void router.navigate({
                                to: '/admin/voice-evals',
                                search: {
                                    ...search,
                                    clean_only: checked || undefined,
                                    page: 1,
                                },
                            })
                        }
                    />
                    <Label htmlFor="clean-only" className="text-sm font-normal">
                        Clean checkouts only
                    </Label>
                </div>
                <Input
                    key={`model-${search.model ?? ''}`}
                    defaultValue={search.model ?? ''}
                    placeholder="Exact model"
                    className="h-8 w-44"
                    onKeyDown={(event) => {
                        if (event.key === 'Enter') {
                            event.currentTarget.blur();
                        }
                    }}
                    onBlur={(event) => {
                        const model =
                            event.currentTarget.value.trim() || undefined;
                        if (model !== search.model) {
                            void router.navigate({
                                to: '/admin/voice-evals',
                                search: { ...search, model, page: 1 },
                            });
                        }
                    }}
                />
                <Input
                    key={`commit-${search.git_commit ?? ''}`}
                    defaultValue={search.git_commit ?? ''}
                    placeholder="Exact commit SHA"
                    className="h-8 w-44 font-mono"
                    onKeyDown={(event) => {
                        if (event.key === 'Enter') {
                            event.currentTarget.blur();
                        }
                    }}
                    onBlur={(event) => {
                        const git_commit =
                            event.currentTarget.value.trim() || undefined;
                        if (git_commit !== search.git_commit) {
                            void router.navigate({
                                to: '/admin/voice-evals',
                                search: { ...search, git_commit, page: 1 },
                            });
                        }
                    }}
                />
                {search.git_branch && (
                    <Button
                        variant="outline"
                        size="sm"
                        onClick={() =>
                            void router.navigate({
                                to: '/admin/voice-evals',
                                search: {
                                    ...search,
                                    git_branch: undefined,
                                    page: 1,
                                },
                            })
                        }
                    >
                        Branch: {search.git_branch} ✕
                    </Button>
                )}
                {search.created_by_user_id && (
                    <Button
                        variant="outline"
                        size="sm"
                        onClick={() =>
                            void router.navigate({
                                to: '/admin/voice-evals',
                                search: {
                                    ...search,
                                    created_by_user_id: undefined,
                                    page: 1,
                                },
                            })
                        }
                    >
                        Creator: {search.created_by_user_id.slice(0, 8)}… ✕
                    </Button>
                )}
            </div>
            <div className="border-border flex items-center justify-between border-y px-4 py-2">
                <span className="text-muted-foreground text-sm">
                    {total.toLocaleString()} runs
                </span>
                <div className="flex items-center gap-2">
                    <Button
                        variant="outline"
                        size="sm"
                        onClick={() =>
                            void router.navigate({
                                to: '/admin/voice-evals',
                                search: { ...search, page: page - 1 },
                            })
                        }
                        disabled={page <= 1}
                    >
                        <ChevronLeft className="h-4 w-4" />
                    </Button>
                    <span className="text-sm">
                        Page {page} of {totalPages}
                    </span>
                    <Button
                        variant="outline"
                        size="sm"
                        onClick={() =>
                            void router.navigate({
                                to: '/admin/voice-evals',
                                search: { ...search, page: page + 1 },
                            })
                        }
                        disabled={page >= totalPages}
                    >
                        <ChevronRight className="h-4 w-4" />
                    </Button>
                </div>
            </div>
            <div className="flex-1 overflow-auto p-4">
                <DataTable
                    columns={columns}
                    data={items}
                    isLoading={isLoading}
                    hidePagination={true}
                    getRowNavigation={(row) => {
                        const routeOptions = {
                            to: '/admin/voice-evals/runs/$runId',
                            params: { runId: row.id },
                        } as const;
                        return {
                            href: router.buildLocation(routeOptions).href,
                            navigate: () => {
                                void router.navigate(routeOptions);
                            },
                        };
                    }}
                    renderEmptyState={() => (
                        <div className="text-muted-foreground py-12 text-center text-sm">
                            No eval runs yet. Launch one with
                            <code className="bg-muted mx-1 rounded px-1 py-0.5 font-mono text-xs">
                                bazel run //evals:cloud -- run voice_agent
                                --family pipeline_replay
                            </code>
                            and its durable result will appear here.
                        </div>
                    )}
                />
            </div>
        </div>
    );
}
